import React, { useState, useEffect, useCallback } from 'react';
import CircledIcon from './CircledIcon';
import { Button } from '@wwe-portal/ui/components/button';
import RangeSlider from '../rangeSlider';
import { useTranslations } from 'next-intl';
import { calculateAbilityDamage } from '../../utils/superstar-details/helper';
import { cn } from '@wwe-portal/ui/lib/lib';
import { useResponsive } from '@wwe-portal/ui/hooks/use-responsive';

interface WrestlerAbilityProps {
  abilityGemImage: string;
  abilityMovePoint: number;
  isEquip: boolean;
  abilityMaxLevel: number;
  abilityName: string;
  abilityID: string;
  onAbilityEquipChange: (
    localIsEquip: boolean,
    abilityID: string,
    abilityLevelValue: number
  ) => void;
  onAbilityLevelChange: (abilityId: string, level: number) => void;
  tier: string;
  starRarity: string;
  wrestlerData: any;
  isDisable: boolean;
}

const WrestlerAbility: React.FC<WrestlerAbilityProps> = ({
  abilityGemImage,
  abilityMovePoint,
  isEquip,
  abilityMaxLevel,
  abilityName,
  abilityID,
  onAbilityEquipChange,
  onAbilityLevelChange,
  tier,
  starRarity,
  wrestlerData,
  isDisable,
}) => {
  /**
   * Use translation hook
   * @see https://react.i18next.com/
   */
  const t = useTranslations();

  /**
   * Set Is Equip for Ability
   */
  const [localIsEquip, setLocalIsEquip] = useState(isEquip);

  /**
   * Set Ability range slider value
   */
  const [abilityLevelValue, setAbilityLevelValue] = useState(abilityMaxLevel);

  /**
   * Set Ability range slider max value
   */
  const [abilityLevelMaxValue, setAbilityLevelMaxValue] =
    useState(abilityMaxLevel);

  /**
   * Set Ability Damage text/value
   */
  const [abilityDamage, setAbilityDamage] = useState('');

  useEffect(() => {
    const damage = calculateAbilityDamage(
      abilityID,
      abilityLevelValue,
      tier,
      starRarity,
      wrestlerData
    );
    setAbilityDamage(damage);
  }, [abilityID, abilityLevelValue, tier, starRarity, wrestlerData]);

  useEffect(() => {
    setAbilityLevelValue(abilityMaxLevel);
    setAbilityLevelMaxValue(abilityMaxLevel);
  }, [abilityMaxLevel, starRarity]);

  useEffect(() => {
    setLocalIsEquip(isEquip);
    // eslint-disable-next-line
  }, [starRarity, tier]);

  const handleAbilityEquipChange = () => {
    setLocalIsEquip(!localIsEquip);
    onAbilityEquipChange(!localIsEquip, abilityID, abilityLevelValue);
  };

  const handleAbilityLevelChange = useCallback((level: number) => {
    setTimeout(() => {
      setAbilityLevelValue(level);
      onAbilityLevelChange(abilityID, level);
    }, 0);
  }, []);
  const { isMobile } = useResponsive();

  return (
    <div
      className={cn('bg-back400 mb-2.5 flex rounded-md', {
        'flex-col': isMobile,
      })}
    >
      <div>
        <div
          className={cn(
            'flex-first border-back200 flex flex-col items-center justify-center border-b-[1px] border-solid p-5 md:border-b-0 md:border-r-[1px]'
          )}
        >
          <CircledIcon icon={abilityGemImage} />
          <p className="text-text500 mb-2.5 text-sm">0 / {abilityMovePoint}</p>
          <Button
            onClick={() => {
              if (isDisable) {
                return !localIsEquip && handleAbilityEquipChange();
              }
              handleAbilityEquipChange();
            }}
            className={cn('flex-first-button w-[85px] p-2', {
              'text-blue700 border-[1px] border-solid border-[#00baff]':
                localIsEquip,
            })}
            data-id={`superstar_ability_${localIsEquip}_button`}
            variant={localIsEquip ? 'none' : 'blue'}
          >
            {localIsEquip ? t('equip') : t('equipped')}
          </Button>
        </div>
      </div>
      <div className="w-full">
        <div className="flex-second w-full p-5">
          <p className="text-text400 text-sm font-medium">{abilityName}</p>
          <p className="text-text600 mb-5 text-xs">{abilityDamage}</p>
          <div className="flex-second__slider-container">
            <RangeSlider
              onRangeSliderChange={handleAbilityLevelChange}
              max={abilityLevelMaxValue}
              min={1}
              value={abilityLevelValue}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default WrestlerAbility;
